import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB, es } from 'date-fns/locale';
import { useLanguage } from '../../i18n/LanguageContext';
import { useAuth } from '../../hooks/useAuth';
import { WorkoutSet } from '../../types';
import { getDateKey, API_URL } from '../../utils';
import { commonDialogStyles } from '../common/dialogStyles';

interface CloneWorkoutsDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (sourceDate: string, workouts: WorkoutSet[]) => void;
  currentDate: Date;
  isSaving: boolean;
  initialDate?: Date | null;
}

export const CloneWorkoutsDialog: React.FC<CloneWorkoutsDialogProps> = ({
  open,
  onClose,
  onSave,
  currentDate,
  isSaving,
  initialDate
}) => {
  const { t, language } = useLanguage();
  const { fetchWithAuth } = useAuth();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [workoutsPreview, setWorkoutsPreview] = useState<WorkoutSet[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dateLocale = language === 'es' ? es : enGB;

  // Set initial date when dialog opens
  useEffect(() => {
    if (open && initialDate) {
      setSelectedDate(initialDate);
    }
  }, [open, initialDate]);

  useEffect(() => {
    if (!selectedDate) {
      setWorkoutsPreview([]);
      setError(null);
      return;
    }

    const loadWorkoutsForDate = async () => {
      setLoading(true);
      setError(null);
      try {
        const dateKey = getDateKey(selectedDate);
        const response = await fetchWithAuth(`${API_URL}/workouts?date=${dateKey}`);
        if (!response.ok) {
          throw new Error('Failed to fetch workouts');
        }
        const data = await response.json();
        setWorkoutsPreview(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to fetch workouts for selected date:', error);
        setError('Failed to load workouts for selected date');
        setWorkoutsPreview([]);
      } finally {
        setLoading(false);
      }
    };

    loadWorkoutsForDate();
  }, [selectedDate, fetchWithAuth]);

  const handleSave = () => {
    if (selectedDate && workoutsPreview.length > 0) {
      onSave(getDateKey(selectedDate), workoutsPreview);
    }
  };

  const handleClose = () => {
    setSelectedDate(null);
    setWorkoutsPreview([]);
    setError(null);
    onClose();
  };

  const canSave = selectedDate && workoutsPreview.length > 0 && !loading && !isSaving;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth sx={commonDialogStyles}>
      <DialogTitle>
        {t('cloneWorkouts.title')} - {currentDate.toLocaleDateString()}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t('cloneWorkouts.selectDate')}
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
            <DatePicker
              label={t('cloneWorkouts.sourceDate')}
              value={selectedDate}
              onChange={(newValue) => setSelectedDate(newValue)}
              maxDate={new Date()}
              sx={{ width: '100%' }}
            />
          </LocalizationProvider>
        </Box>

        {selectedDate && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              {t('cloneWorkouts.workoutsPreview')}
            </Typography>
            
            {loading ? (
              <Box display="flex" justifyContent="center" sx={{ my: 2 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Typography color="error" sx={{ my: 2 }}>
                {error}
              </Typography>
            ) : workoutsPreview.length === 0 ? (
              <Typography color="text.secondary" sx={{ my: 2 }}>
                {t('cloneWorkouts.noWorkouts')}
              </Typography>
            ) : (
              <List sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {workoutsPreview.map((workout, index) => (
                  <React.Fragment key={workout.id || index}>
                    <ListItem>
                      <ListItemText
                        primary={workout.exercise}
                        secondary={
                          <Box>
                            {workout.details.map((detail, detailIndex) => (
                              <Typography key={detailIndex} variant="body2" component="div">
                                {t('workout.series')} {detail.series}: {detail.reps} {t('workout.reps')} × {detail.weight} kg
                                {detail.rest_time_seconds != null && ` | ${t('workout.restTime')}: ${detail.rest_time_seconds}s`}
                                {detail.rir && ` | ${t('workout.rir')}: ${detail.rir}`}
                              </Typography>
                            ))}
                            {workout.comment && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {t('workout.comment')}: {workout.comment}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < workoutsPreview.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          {t('common.cancel')}
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={!canSave}
        >
          {isSaving ? <CircularProgress size={20} /> : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 